import { useCallback, useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { useToast } from '@/providers/UiProviders'
import { ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import {
  fetchComponents,
  createComponent,
  updateComponent,
  deleteComponent,
  fetchPublicStatus,
  COMPONENT_STATUSES,
  statusMeta,
  SP_STR as S,
  type StatusComponent,
  type PublicStatus,
} from './statusPageApi'

const sevColor: Record<string, string> = { sev1: 'text-danger', sev2: 'text-warning', sev3: 'text-secondary', sev4: 'text-tertiary' }

export function StatusPageScreen() {
  const { toast } = useToast()
  const [rows, setRows] = useState<StatusComponent[] | null>(null)
  const [pub, setPub] = useState<PublicStatus | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchComponents(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    fetchPublicStatus(ac.signal).then(setPub).catch(() => setPub(null))
    return () => ac.abort()
  }, [tick])

  const add = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      await createComponent(name.trim(), '')
      setName('')
      toast(S.done, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const setStatus = async (c: StatusComponent, status: string) => {
    setRows((prev) => prev?.map((x) => (x.id === c.id ? { ...x, status } : x)) ?? prev)
    try {
      await updateComponent(c.id, { status })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
      reload()
    }
  }
  const del = async (id: number) => {
    try {
      await deleteComponent(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  const overallMeta = pub ? statusMeta(pub.overall) : null

  return (
    <>
      <PageHeader title={S.title} screenId="A17.28" description={S.desc} />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {/* Overall */}
          {overallMeta && (
            <div className="flex items-center gap-2 rounded-lg border border-border-default bg-surface-2 px-4 py-3">
              <span className={`h-2.5 w-2.5 rounded-full ${overallMeta.dot}`} />
              <span className="text-sm text-tertiary">{S.overall}:</span>
              <span className={`text-sm font-medium ${overallMeta.color}`}>{overallMeta.label}</span>
            </div>
          )}

          {err && <ErrorState code={err.code} onRetry={reload} />}

          {/* Components */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && add()}
                placeholder={S.newComponent}
                className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-2 text-sm text-primary outline-none focus:border-accent"
              />
              <button type="button" onClick={add} disabled={busy || !name.trim()} className="flex items-center gap-1 rounded-md bg-accent px-3 py-2 text-sm text-white disabled:opacity-50">
                <Plus size={14} />
                {S.add}
              </button>
            </div>
            {!rows ? (
              <div className="space-y-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Skeleton key={i} className="h-10 w-full" />
                ))}
              </div>
            ) : rows.length === 0 ? (
              <p className="py-6 text-center text-sm text-tertiary">{S.none}</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-default">
                {rows.map((c, i) => {
                  const m = statusMeta(c.status)
                  return (
                    <div key={c.id} className={`flex items-center gap-3 px-4 py-2.5 ${i > 0 ? 'border-t border-border-subtle' : ''}`}>
                      <span className={`h-2.5 w-2.5 shrink-0 rounded-full ${m.dot}`} />
                      <span className="min-w-0 flex-1 truncate text-sm text-primary">{c.name}</span>
                      <select
                        value={c.status}
                        onChange={(e) => setStatus(c, e.target.value)}
                        className={`rounded-md border border-border-default bg-surface-1 px-2 py-1 text-xs ${m.color}`}
                      >
                        {COMPONENT_STATUSES.map((s) => (
                          <option key={s.key} value={s.key}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                      <button type="button" onClick={() => del(c.id)} className="shrink-0 rounded-md p-1.5 text-tertiary hover:bg-surface-3 hover:text-danger" title={S.delete}>
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Active incidents (from public /status, read-only) */}
          <div>
            <p className="mb-2 text-xs font-medium text-secondary">{S.activeIncidents}</p>
            {!pub ? (
              <Skeleton className="h-8 w-full" />
            ) : pub.active_incidents.length === 0 ? (
              <p className="rounded-lg border border-border-default bg-surface-2 px-4 py-3 text-sm text-tertiary">{S.noIncidents}</p>
            ) : (
              <div className="overflow-hidden rounded-lg border border-border-default">
                {pub.active_incidents.map((inc, i) => (
                  <div key={inc.id} className={`flex items-center gap-3 px-4 py-2 ${i > 0 ? 'border-t border-border-subtle' : ''}`}>
                    <span className={`font-mono text-xs ${sevColor[inc.severity] ?? ''}`}>{inc.severity.toUpperCase()}</span>
                    <span className="min-w-0 flex-1 truncate text-sm text-primary">{inc.title}</span>
                    <span className="shrink-0 text-xs text-tertiary">{inc.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
